import { LoadingButton } from '@mui/lab';
import { Box, Button, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { driver } from 'driver.js';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralDatePicker, GeneralSelectMenu, GeneralTimePicker } from 'src/components/general';
import GeneralUpload from 'src/components/general/GeneralUpload';
import { FormProvider, RHFRadioGroup, RHFTextField } from 'src/components/hook-form';
import { APP_BANNER_LIST } from 'src/constants/queryKeys';
import JM from 'jalali-moment';

import {
  networkCreateNotificationBanner,
  networkUpdateNotificationBanner,
  useFetchNotificationBannerTypeList,
  useFetchNotificationBannerValueType,
} from 'src/network/notification-banner-list';
import useFormsModalStore from 'src/store/forms';
import { getDefaultValues } from './defaultValues';
import { generateSelectMenuItems } from 'src/utils';
import GeneralMediaUploader from 'src/components/general/GeneralMediaUploader';

const NotificationBannerForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();
  const { projectId, sectionKeyOrder, typeId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const bannerDetails = modal?.data;
  const isEditMode = !!bannerDetails;
  const bannerId = bannerDetails?.NotificationBannerID;

  const GENDER_OPTION = ['نمایش عمومی', 'نمایش فقط به کاربران عضو شده'];

  const defaultValues = getDefaultValues(bannerDetails);
  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    trigger,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [startDate, setStartDate] = useState(bannerId ? new Date(defaultValues?.NotificationBannerDateStart) : new Date());
  const [endDate, setEndDate] = useState(bannerId ? new Date(defaultValues?.NotificationBannerDateExpire) : new Date());

  const [startTime, setStartTime] = useState(bannerId ? new Date(defaultValues?.NotificationBannerTimeStart) : new Date().setHours(0, 0, 0, 0));
  const [endTime, setEndTime] = useState(bannerId ? new Date(defaultValues?.NotificationBannerTimeEnd) : new Date().setHours(23, 59, 59, 999));

  const [manipulatedStartTime, setManipulatedStartTime] = useState(
    defaultValues?.NotificationBannerTimeStart || JM(new Date().setHours(0, 0, 0, 0)).format('HH:mm:ss')
  );
  const [manipulatedEndTime, setManipulatedEndTime] = useState(
    defaultValues?.NotificationBannerTimeEnd || JM(new Date().setHours(23, 59, 59, 999)).format('HH:mm:ss')
  );

  const [bannerTypes, setBannerTypes] = useState([]);
  const [selectedBannerType, setSelectedBannerType] = useState('');

  const [bannerValueTypes, setBannerValueTypes] = useState([]);
  const [selectedBannerValueType, setSelectedBannerValueType] = useState('');

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      NotificationBannerApplicationID: +projectId,
      NotificationBannerNotificationBannerLinkTypeID: 1,
      NotificationBannerNotificationBannerCloseModeID: 1,
      NotificationBannerNotificationBannerValueTypeID: selectedBannerValueType,
      NotificationBannerDateStart: new Date(startDate).toISOString(),
      NotificationBannerDateExpire: new Date(endDate).toISOString(),
      NotificationBannerTimeStart: manipulatedStartTime,
      NotificationBannerTimeEnd: manipulatedEndTime,
      NotificationBannerShowOnlyForProfile: values?.NotificationBannerShowOnlyForProfile === 'نمایش عمومی' ? 0 : 1,
      NotificationBannerOrderIndex: +values?.NotificationBannerOrderIndex,
      NotificationBannerStatus: values?.NotificationBannerStatus ? 1 : 0,
      ApplicationSectionKeyOrder: +sectionKeyOrder,
      NotificationBannerNotificationBannerTypeID: selectedBannerType,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateNotificationBanner(bannerId, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('بنر موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [APP_BANNER_LIST, projectId] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateNotificationBanner(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('بنر موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [APP_BANNER_LIST, projectId] });
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const driverInstance = driver({
    popoverClass: 'driverjs-theme',
    stagePadding: 0,
    overlayColor: 'transparent',
    onDestroyed: () => {
      document?.activeElement?.blur();
    },
  });

  const { data: bannerTypesResult, isLoading: bannerTypeLoading } = useFetchNotificationBannerTypeList();

  const { data: bannerValueTypesResult, isLoading: bannerValueTypeLoading } = useFetchNotificationBannerValueType();

  useEffect(() => {
    if (bannerTypesResult && bannerValueTypesResult) {
      const bannerTypes = generateSelectMenuItems(
        bannerTypesResult?.data?.items,
        'NotificationBannerTypeLable',
        'NotificationBannerTypeID'
      )?.reverse();

      const bannerValueTypes = generateSelectMenuItems(
        bannerValueTypesResult?.data?.items,
        'NotificationBannerValueTypeLable',
        'NotificationBannerValueTypeID'
      );

      setBannerTypes(bannerTypes);
      setSelectedBannerType(bannerDetails?.NotificationBannerTypeID || bannerTypes[0]?.id);

      setBannerValueTypes(bannerValueTypes);
      setSelectedBannerValueType(bannerDetails?.NotificationBannerValueTypeID || bannerValueTypes[0]?.id);
    }
  }, [bannerTypesResult, bannerValueTypesResult]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="NotificationBannerTitle" type="text" label="عنوان بنر در پروژه" />

              <RHFTextField name="NotificationBannerTitlePanel" type="text" label="عنوان بنر در پنل" />

              {!bannerTypeLoading && bannerTypes ? (
                <GeneralSelectMenu
                  label="نوع نمایش بنر"
                  name="NotificationBannerNotificationBannerTypeID"
                  options={bannerTypes}
                  value={selectedBannerType}
                  onChangeValue={setSelectedBannerType}
                />
              ) : null}

              {!bannerValueTypeLoading && bannerValueTypes ? (
                <GeneralSelectMenu
                  label="نوع بنر"
                  name="NotificationBannerValueTypeID"
                  options={bannerValueTypes}
                  value={selectedBannerValueType}
                  onChangeValue={setSelectedBannerValueType}
                />
              ) : null}

              <GeneralUpload
                label="تصویر بنر"
                id="notification_nanner_value"
                className="notification_nanner_value"
                name="NotificationBannerValue"
                afterChangeAction={(res) => {
                  setValue('NotificationBannerValue', res);
                  trigger('NotificationBannerValue');
                }}
                onFocus={(e) =>
                  driverInstance.highlight({
                    element: e?.target,
                    popover: {
                      title: 'فیلد وردی آپلود عکس ',
                      description:
                        'لطفاً تصویری با نسبت 16:9 آپلود کنید. این تصویر باید به صورت افقی باشد و عرض آن باید نسبت به ارتفاع 16 برابر باشد. برای بهترین نتیجه، از تصاویری با کیفیت بالا استفاده کنید.',
                    },
                  })
                }
              />

              {selectedBannerValueType === 4 ? (
                <GeneralMediaUploader
                  label="آپلود فایل ویدیویی"
                  id="notification-banner-link-mp4"
                  name="NotificationBannerLinkMp4"
                  afterChangeAction={(res) => {
                    setValue('NotificationBannerLinkMp4', res);
                    trigger('NotificationBannerLinkMp4');
                  }}
                />
              ) : null}

              <RHFTextField name="NotificationBannerTextLink" type="text" label="عنوان دکمه بنر" />

              {selectedBannerValueType === 4 && <div />}

              <GeneralDatePicker label="تاریخ شروع" name="NotificationBannerDateStart" value={startDate} onChangeValue={setStartDate} />

              <GeneralDatePicker
                minDate={startDate}
                label="تاریخ پایان"
                name="NotificationBannerDateExpire"
                value={endDate}
                onChangeValue={setEndDate}
              />

              <GeneralTimePicker
                label="ساعت شروع نمایش"
                name="NotificationBannerTimeStart"
                value={startTime}
                onChangeValue={setStartTime}
                setManipulatedStartTime={setManipulatedStartTime}
                type="start"
              />

              <GeneralTimePicker
                label="ساعت پایان نمایش"
                name="NotificationBannerTimeEnd"
                value={endTime}
                onChangeValue={setEndTime}
                setManipulatedEndTime={setManipulatedEndTime}
                type="end"
              />

              <RHFTextField name="NotificationBannerLink" type="text" label="لینک دامنه محتوا" placeholder="https://example.com" />

              <RHFTextField name="NotificationBannerOrderIndex" type="text" label="ترتیب نمایش" />

              <div className="flex flex-col gap-2">
                <Typography variant="button">نحوه نمایش</Typography>
                <RHFRadioGroup
                  name="NotificationBannerShowOnlyForProfile"
                  options={GENDER_OPTION}
                  sx={{
                    '& .MuiFormControlLabel-root': { mr: 4 },
                  }}
                />
              </div>

              <FormControlLabel
                name="NotificationBannerStatus"
                label={'آیا نمایش داده شود؟'}
                control={
                  <Switch
                    checked={methods.watch('NotificationBannerStatus')}
                    onChange={(e) => {
                      setValue('NotificationBannerStatus', e.target.checked ? 1 : 0);
                      trigger('NotificationBannerStatus');
                    }}
                  />
                }
              />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default NotificationBannerForm;
